import {
    createContext,
    useContext,
    useEffect,
    useState,
    PropsWithChildren,
    useMemo
} from "react";

import { useUserContext } from "@/components/user-context";
import { UserProfileResponse } from "@/libraries/bxp-web-stargate-client/src";
import { AllJiraSitesResponseType } from "@/libraries/logged-in-user-jira-sites-client/all-jira-sites.schema.ts";
import { LoggedInUserJiraSitesClient } from "@/libraries/logged-in-user-jira-sites-client/logged-in-user-jira-sites-client.ts";

type JiraSitesContextType = {
    jiraSitesFromAgg?: AllJiraSitesResponseType;
};

const JiraSitesContext = createContext<JiraSitesContextType | null>(null);

export const useJiraSitesContext = () => {
    const context = useContext(JiraSitesContext);
    if (!context) {
        throw new Error(
            "useJiraSitesContext must be used within a JiraSitesProvider"
        );
    }
    return context;
};

export const JiraSitesProvider = ({
    children
}: PropsWithChildren<{ userProfile?: UserProfileResponse }>) => {
    const [jiraSitesFromAgg, setJiraSitesFromAgg] = useState<
        AllJiraSitesResponseType | undefined
    >(undefined);
    const { profile } = useUserContext();
    const aggClient = useMemo(() => new LoggedInUserJiraSitesClient(), []);

    useEffect(() => {
        if (
            !profile.attributes?.existingSites ||
            profile.attributes.existingSites.length !== 1
        ) {
            return;
        }

        const site = profile.attributes.existingSites[0];

        aggClient.getJiraSites(site?.cloudId ?? "").then((data) => {
            setJiraSitesFromAgg(data);
        });
    }, [aggClient, profile.attributes?.existingSites]);

    return (
        <JiraSitesContext.Provider value={{ jiraSitesFromAgg }}>
            {children}
        </JiraSitesContext.Provider>
    );
};
